import type { PushProvider } from '@/features/push-credentials';
import { useNavigationGuard } from '@/services/navigation-guard';
import { usePushSettingsErrors } from '../hooks';
import { hasConfigError, pushErrorsOrFallback, REMOVE_FAILED_MESSAGE } from '../utils/push-errors';
import type { PushProviderFormProps, PushProviderFormState } from '../types';
import { FcmFieldsView } from './fcm-fields-view';
import { usePushCredentialWrites, usePushSaveRunner } from './push-credential-writes';
import { useFcmDraft } from './use-fcm-draft';

export type UseFcmFormOptions = PushProviderFormProps & {
  providers: readonly PushProvider[];
};

export function useFcmForm({
  workspaceId,
  platform,
  platforms,
  providers,
  credential,
  isEditing,
  isReading,
  canEdit,
  onEditingChange,
  onCancel,
  onSaved,
  onDisabled,
}: UseFcmFormOptions): PushProviderFormState {
  const errors = usePushSettingsErrors();
  const writes = usePushCredentialWrites(workspaceId);
  const runSave = usePushSaveRunner(errors.addErrors);

  const isEnabled = credential != null;
  const isBusy = isReading || writes.isWriting;
  const draft = useFcmDraft({ platform, platforms, providers, isEnabled, isEditing, writes });

  useNavigationGuard({ hasUnsavedChanges: draft.isDirty });

  const finishSave = (context: Parameters<typeof onSaved>[0]) => {
    draft.clearFile();
    onEditingChange(false);
    onSaved(context);
  };

  const save = () => {
    if (!draft.hasKeyToSave) {
      draft.refuse();
      return;
    }
    const { context, write } = draft.prepareSave();
    void runSave({ write, onSaved: () => finishSave(context) });
  };

  const cancel = () => {
    draft.clearFile();
    onCancel();
  };

  const disable = async () => {
    try {
      await writes.remove('push_fcm');
    } catch (error) {
      errors.addErrors('config', pushErrorsOrFallback(error, REMOVE_FAILED_MESSAGE));
      return;
    }
    onDisabled();
  };

  return {
    provider: 'fcm',
    isEnabled,
    isSaving: writes.isWriting,
    isBusy,
    sandbox: false,
    isComplete: draft.hasKeyToSave,
    isDirty: draft.isDirty,
    errors,
    getCredentials: draft.getCredentials,
    fields: (
      // The key is this section's only field, so nothing else spaces it from the save bar.
      <FcmFieldsView
        ref={draft.fieldsRef}
        mb="200"
        file={draft.file}
        savedFileName={credential?.username ?? null}
        isStoredKeyOffer={draft.isStoredKeyOffer}
        isEditing={isEditing}
        canEdit={canEdit}
        isBusy={isBusy}
        isInvalid={hasConfigError(errors.errors)}
        fileError={draft.fileError}
        onFileSelected={draft.setFile}
      />
    ),
    save,
    cancel,
    disable,
  };
}
